import { useContext, useMemo, useState, type ChangeEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { MessageSquare, PhoneOutgoing } from "lucide-react";

import { Compass } from "~/components/intake/Compass";
import { DispositionChip } from "~/components/intake/DispositionChip";
import { HandoffCard, HandoffChip, handoffCardKey } from "~/components/intake/HandoffCard";
import { HandoffStatus } from "~/components/intake/HandoffStatus";
import { Intake } from "~/components/intake/Intake";
import { OUTCOME_COPY, OutcomeForm, type OutcomeMode } from "~/components/intake/OutcomeForm";
import { NoteCanvas } from "~/components/intake/NoteCanvas";
import { RetainerForm } from "~/components/intake/RetainerForm";
import { RetainerChip, RetainerLine } from "~/components/intake/RetainerLine";
import { StageChip } from "~/components/intake/StageChip";
import { FormPane } from "~/components/intake/FormPane";
import { MessagePane } from "~/components/intake/MessagePane";
import { CallAction } from "~/components/intake/CallAction";
import { CaseThreads } from "~/components/intake/CaseThreads";
import { ConsentForm } from "~/components/intake/ConsentForm";
import { RecordingPlayer } from "~/components/intake/RecordingPlayer";
import { TranscriptPane } from "~/components/intake/TranscriptPane";
import { taskClock } from "~/lib/taskClock";
import { useNow } from "~/lib/useNow";
import { Alert, Button, Card, Dialog, Input, TimerChip } from "~/components";
import { heading } from "~/components/Card.css";
import {
  caseFunnel,
  caseProject,
  caseQueryOptions,
  CHOOSE_RETAINER_TASK_TYPE,
  FIRST_CONTACT_TASK_TYPE,
  formatValidationError,
  isDeadEnd,
  isValidationError,
  useCaseTaskStateMutation,
  useUpdateCaseMutation,
  type Case,
  type CaseFunnel,
} from "~/lib/cases";
import { contactName, useUpdateContactMutation, type Contact } from "~/lib/contacts";
import {
  newestRetainer,
  useCreateRetainerMutation,
  type Retainer,
  type RetainerDelivery,
  type RetainerVariant,
} from "~/lib/retainers";
import {
  threadsListQueryOptions,
  caseThreads,
  threadQueryOptions,
  useCreateThreadMutation,
  useOpenMatter,
  type Thread,
} from "~/lib/threads";
import {
  useSendMessageMutation,
} from "~/lib/messages";
import type { Disposition } from "~/lib/dispositions";
import {
  consentScriptQueryOptions,
  offersVerbalCapture,
  textingBlocked,
} from "~/lib/textingConsent";
import { caseNotesKey } from "~/lib/draftStore";
import { formatPhone } from "~/lib/phones";
import { queryClient } from "~/lib/queryClient";
import { VoiceDeviceContext } from "~/lib/voice/useVoiceDevice";

import * as styles from "./_app.alpha.threads.$threadId.css";

// The matter workspace: the focused thread's (channel, state) pair owns the
// stage layout (DESIGN.md §5); the frame around it (header strip, context
// rail, action bar) never changes, which is what keeps reconfiguration
// from being disorienting.
//
// WHICH MATTER a rep is on is the thread's to say: the thread view embeds its
// case and the person that case talks to. The plan is not on that embed, so
// the matter itself is read through its own view, which carries the project's
// task rows and the agreements the funnel grew (TMF-360).
//
// The matter moves two ways (TMF-335) and the action bar offers whichever one
// is live: the ordinary manual tasks complete by EVENT, while qualification
// and close-out are CAPTURES, one case PUT each, off which the server
// completes the task. Nothing here asserts a stage; the header shows the
// column the API projected.
export const Route = createFileRoute("/_app/alpha/threads/$threadId")({
  loader: async ({ params }) => {
    const thread = await queryClient.ensureQueryData(threadQueryOptions(params.threadId));
    const caseId = thread._embedded?.case?.id;
    if (caseId !== undefined) {
      await queryClient.ensureQueryData(caseQueryOptions(caseId));
    }
  },
  component: WorkspaceRoute,
});

function WorkspaceRoute() {
  const { threadId } = Route.useParams();
  const { data: thread } = useSuspenseQuery(threadQueryOptions(threadId));
  const { data: threadCol } = useSuspenseQuery(threadsListQueryOptions);
  const now = useNow();
  const phone = useContext(VoiceDeviceContext);
  const openMatter = useOpenMatter();

  const caseId = thread._embedded?.case?.id;
  // The matter with its plan, its task rows and its agreements. The thread's
  // own embed carries the row and its person only, which is enough to know
  // WHICH matter this is and not enough to know what may be done on it.
  const { data: matter } = useQuery({
    ...caseQueryOptions(caseId ?? ""),
    enabled: caseId !== undefined,
  });

  // Every conversation this matter has had. Derived BEFORE the partial-payload
  // guard below, because a hook cannot sit behind a return, and read here
  // rather than inside the strip because the column's top inset depends on
  // whether the strip is there (see stageContentWithThreads): one reader, one
  // answer. The collection is the rail's, already fetched and cached by the
  // layout's loader.
  const conversations = useMemo(
    () => (caseId === undefined ? [] : caseThreads(threadCol._embedded ?? [], caseId)),
    [threadCol, caseId],
  );

  if (matter === undefined) {
    // A thread always has a matter server-side; this is a partial-payload
    // guard, not a business state.
    return <div className={styles.stageContent}>Matter unavailable for this thread.</div>;
  }

  const contact = matter._embedded?.contact;
  // The person this conversation is with, whose consent a text on it asks: the
  // matter's contact when it was opened, which the matter may since have moved
  // on from.
  const conversant = thread._embedded?.contact;
  const funnel = caseFunnel(matter);
  const project = caseProject(matter);
  const settled = isDeadEnd(matter.stage);
  // The agreement this matter is on: the newest row, because a supersession
  // leaves the older one voided and its arm cancelled.
  const retainer = newestRetainer(matter);

  // The header clock: speed-to-lead while first contact hasn't happened
  // (that's the clock this product exists to stop), thread quiet-time after.
  const clock = taskClock(funnel.firstContactTask, now);
  const quietSinceMs = now - new Date(thread.last_activity_at ?? matter.created_at).getTime();

  return (
    <div className={styles.workspace}>
      <header className={styles.header}>
        <span className={styles.personName}>{contactName(contact)}</span>

        <div className={styles.headerGroup}>
          {settled ? (
            <DispositionChip disposition={matter.disposition} />
          ) : matter.stage !== null ? (
            <StageChip name={matter.stage} />
          ) : null}
          <HandoffChip handoff={funnel.handoff} />
          {/* The refused agreement, or the one that never went out, said
              beside the stage rather than instead of it: neither moves the
              matter, and the chip is what tells the rep to send another
              (TMF-407). WHETHER it shows is the plan's, off the signature
              and send rows; the ROW supplies the wording. */}
          {(funnel.retainerRejected || funnel.sendFailed) && retainer !== null ? (
            <RetainerChip retainer={retainer} />
          ) : null}
          {retainer !== null ? <RetainerLine retainer={retainer} /> : null}
        </div>

        <div className={styles.headerSpacer} />

        <div className={styles.timerBlock}>
          <TimerChip
            elapsedMs={clock.running ? clock.elapsedMs : quietSinceMs}
            warnMs={clock.warnMs}
            breachMs={clock.breachMs}
            title={clock.running ? "Time since the matter arrived" : "Time since last activity"}
          />
          <span className={styles.timerCaption}>
            {clock.running ? "awaiting first touch" : "since last activity"}
          </span>
        </div>
      </header>

      {/* Keying on (channel, state) makes the reconfiguration cross-fade
          run exactly when the layout actually changes. */}
      <div className={styles.body} key={`${thread.channel}-${thread.state}`}>
        <div
          className={
            conversations.length > 1
              ? `${styles.stageContent} ${styles.stageContentWithThreads}`
              : styles.stageContent
          }
        >
          {/* The matter's other conversations head the STAGE COLUMN, not the
              frame: spanning the full width put a band above the context rail
              and pushed the Compass down the page. In the column it is chrome
              on the work itself, and the rail keeps its own top edge whether
              or not this matter has a second conversation.

              One conversation is nothing to navigate between, and the column's
              inset is what changes with it, so the route owns the decision
              rather than the strip returning null into a layout that already
              made room for it. */}
          {conversations.length > 1 ? (
            <CaseThreads threads={conversations} currentId={thread.id} />
          ) : null}
          <div className={styles.stage}>
            <HandoffCard key={handoffCardKey(funnel.handoff)} matter={matter} handoff={funnel.handoff} />
            <StageBody thread={thread} matter={matter} />
            <Intake
              key={matter.id}
              autosave
              caseRow={matter}
              onOpen={openMatter.open}
              opening={openMatter.opening}
            />
          </div>
        </div>

        <aside className={styles.contextRail} aria-label="Matter context">
          {/* The instrument is a constant (TMF-277), so the Compass is
              always there — there is no form to be missing.

              Keyed by the MATTER, because the rail moves between threads by
              changing this route's params and the route does NOT remount: an
              un-keyed Compass kept one matter's optimistic taps and then wrote
              them on the next one (TMF-348). The answers belong to the matter,
              so its identity is what owns the instance. */}
          <Compass key={matter.id} caseRow={matter} triageable={funnel.triageable} />
          {/* The Compass owns the top of the rail; the details sit open
              below it. */}
          <MatterDetails matter={matter} contact={contact} />
          <HandoffStatus matter={matter} handoff={funnel.handoff} />
        </aside>
      </div>

      <div className={styles.actionBar}>
        {/* The Text action lives here; a text thread's composer is its text
            action. */}
        {thread.channel === "text" ? null : <TextAction contact={contact} matter={matter} />}
        <ConsentAction contact={conversant} />
        <CallAction device={phone} contact={contact} matter={matter} />
        {project ? (
          // Keyed for the same reason as the Compass (TMF-348): the bar holds
          // the outcome dialog's intent and the error of the last write it
          // made, and both of those are statements about ONE matter.
          <FunnelActions
            key={matter.id}
            matter={matter}
            projectId={project.id}
            funnel={funnel}
            retainer={retainer}
          />
        ) : null}
      </div>
    </div>
  );
}

/** What the rep is doing when they open the retainer dialog, whether the plan
 *  is asking for the first agreement or they are replacing one already out.
 *  Both buttons say it, so it is said once. */
const SEND_RETAINER_LABEL = "Send retainer";

/**
 * The matter's live actions, and there are only ever two kinds (TMF-335).
 *
 * ORDINARY WORK posts an event and lets the engine decide what it means: the
 * next open task of the manual chain, first contact and then the retainer
 * chain a qualified matter grew. The first-contact task is labelled "Mark
 * first contact" rather than by its type: one button, not two, for what the
 * CallRail auto-stop would otherwise do on its own.
 *
 * THE CHOICE is the one chain row that does not complete by event: "Choose
 * retainer" opens the retainer dialog, whose POST creates the row and lets the
 * ENGINE complete the task off it (TMF-407). The same button is the resend,
 * because choosing again supersedes the live agreement: it stays while the
 * plan says a retainer may go out (funnel.sendable), by which time the chain
 * has moved past the choice.
 *
 * CAPTURE is a case PUT and nothing else. The qualification answer and the
 * close-out reason are values on the case, and the SERVER publishes the
 * completion of "Qualify lead" / "Close out lead" off the write, so a second
 * write from here would be a second author of one fact. A NO carries its
 * reason in the SAME request, because a disqualified matter composes no
 * further work to record one under.
 *
 * Both captures are ONE-WAY. The answer is capture-once and a recorded
 * close-out is final: there is no un-lose, and a caller who rings back opens a
 * new matter.
 *
 * WHAT IS OFFERED is the PLAN's statement and nothing else: caseFunnel reads
 * the task rows, and this component renders what it says. It does not consult
 * the matter's values to second-guess a row, so an affordance the plan still
 * holds is shown even where the API will refuse the write (a signed matter's
 * "Close out lead" row stays open until "Handoff" cancels it), and the refusal
 * surfaces in the Alert below. A row that has closed takes its buttons with
 * it, one consumer hop after the write that closed it.
 */
function FunnelActions({
  matter,
  projectId,
  funnel,
  retainer,
}: {
  matter: Case;
  projectId: string;
  funnel: CaseFunnel;
  /** The agreement this matter is on, derived once by the route for the header
   *  strip. The dialog names it and says that sending replaces it. */
  retainer: Retainer | null;
}) {
  const changeTaskState = useCaseTaskStateMutation();
  const updateCase = useUpdateCaseMutation();
  const createRetainer = useCreateRetainerMutation();
  const [picking, setPicking] = useState<OutcomeMode | null>(null);
  const [choosing, setChoosing] = useState(false);

  const busy = changeTaskState.isPending || updateCase.isPending || createRetainer.isPending;
  const { nextTask, asks, closeable, sendable } = funnel;
  const choiceIsNext = nextTask?.type === CHOOSE_RETAINER_TASK_TYPE;
  const nextLabel =
    nextTask === null
      ? ""
      : nextTask.type === FIRST_CONTACT_TASK_TYPE
        ? "Mark first contact"
        : choiceIsNext
          ? SEND_RETAINER_LABEL
          : nextTask.type;

  const handleSend = (variant: RetainerVariant, delivery: RetainerDelivery) => {
    createRetainer.mutate(
      { caseId: matter.id, variant, delivery },
      { onSuccess: () => setChoosing(false) },
    );
  };
  // The bar's own writes: the qualifying YES and the task events. The dialog
  // owns the error for the writes it collects.
  const barError = updateCase.error ?? changeTaskState.error;

  /** Stamp the rep's note onto the matter's running record. Empty stays empty:
   *  the outcome is the value the report reads, the note is colour. */
  const withNote = (note: string): { notes?: string } => {
    if (note === "") return {};
    const stamped = `${new Date().toLocaleDateString()}: ${note}`;
    return { notes: matter.notes.trim() === "" ? stamped : `${matter.notes}\n${stamped}` };
  };

  const handleQualify = () => {
    updateCase.mutate({ id: matter.id, qualified: true });
  };

  const handlePicked = (disposition: Disposition, note: string) => {
    updateCase.mutate(
      {
        id: matter.id,
        disposition,
        // The disqualifying answer travels WITH its reason; a close-out is
        // the reason alone, on a matter already answered yes.
        ...(picking === "disqualify" ? { qualified: false } : {}),
        ...withNote(note),
      },
      { onSuccess: () => setPicking(null) },
    );
  };

  return (
    <>
      {nextTask !== null ? (
        <Button
          type="button"
          size="sm"
          disabled={busy}
          onClick={() => {
            if (choiceIsNext) {
              setChoosing(true);
              return;
            }
            changeTaskState.mutate({ projectId, taskId: nextTask.id, state: "complete" });
          }}
        >
          {nextLabel}
        </Button>
      ) : null}
      {/* The resend, offered once the chain has moved past the choice: the
          plan still says an agreement may go out, and choosing again is how
          one does. */}
      {sendable && !choiceIsNext ? (
        <Button type="button" size="sm" disabled={busy} onClick={() => setChoosing(true)}>
          {SEND_RETAINER_LABEL}
        </Button>
      ) : null}
      {asks ? (
        <>
          <Button type="button" size="sm" disabled={busy} onClick={handleQualify}>
            Qualify
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => setPicking("disqualify")}
          >
            Not qualified
          </Button>
        </>
      ) : null}
      {closeable ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={busy}
          title="Record how this matter ended: it cancels the retainer work still open on it"
          onClick={() => setPicking("close-out")}
        >
          Close out
        </Button>
      ) : null}
      {/* The dialog carries its own error; this covers the writes made
          straight from the bar — the qualifying YES and the task events. */}
      {picking === null && barError !== null ? (
        <Alert variant="danger">
          {isValidationError(barError)
            ? formatValidationError(barError)
            : "Couldn't record that — try again."}
        </Alert>
      ) : null}
      {picking !== null ? (
        <Dialog
          title={OUTCOME_COPY[picking].title}
          hint={OUTCOME_COPY[picking].hint}
          onClose={() => setPicking(null)}
        >
          <OutcomeForm
            mode={picking}
            busy={busy}
            error={updateCase.error}
            onCancel={() => setPicking(null)}
            onConfirm={handlePicked}
          />
        </Dialog>
      ) : null}
      {choosing ? (
        <Dialog
          title="Send a retainer"
          hint="The agreement goes out for signature as soon as it is chosen."
          onClose={() => setChoosing(false)}
        >
          <RetainerForm
            current={retainer}
            busy={busy}
            error={createRetainer.error}
            onCancel={() => setChoosing(false)}
            onConfirm={handleSend}
          />
        </Dialog>
      ) : null}
    </>
  );
}

/**
 * Capturing verbal consent from a person who has none on file (TMF-520). The
 * dialog shows the server's script, and the capture is a contact PUT naming the
 * Verbal source: the server records the script it served, the rep and the
 * moment, so nothing the page holds becomes evidence. A person who opted out by
 * text is offered no capture: only their own START lifts it.
 */
function ConsentAction({ contact }: { contact: Contact | undefined }) {
  const [capturing, setCapturing] = useState(false);
  const script = useQuery({ ...consentScriptQueryOptions, enabled: capturing });
  const updateContact = useUpdateContactMutation();

  if (contact === undefined || !offersVerbalCapture(contact)) return null;

  const cancelCapture = () => {
    setCapturing(false);
    updateContact.reset();
  };

  return (
    <>
      <Button type="button" variant="secondary" size="sm" onClick={() => setCapturing(true)}>
        Capture verbal consent
      </Button>
      {capturing ? (
        <Dialog
          title="Capture verbal consent"
          hint="Read this to the person, word for word. Confirm only once they have agreed."
          onClose={cancelCapture}
        >
          <ConsentForm
            script={script.data?.text}
            busy={updateContact.isPending}
            error={updateContact.error}
            onCancel={cancelCapture}
            onConfirm={() =>
              updateContact.mutate(
                { id: contact.id, texting_consent: "Verbal" },
                { onSuccess: () => setCapturing(false) },
              )
            }
          />
        </Dialog>
      ) : null}
    </>
  );
}

/**
 * The reply box (TMF-466). It sends through the server's seam and shows
 * whatever the seam answers.
 *
 * NO OPTIMISTIC INSERT: the row the pane renders is the one the vendor
 * accepted, so the draft clears only once the send has come back, and the
 * conversation is re-read rather than guessed at. A reply the rep watched
 * appear and that never went out is the failure worth spending a round trip on.
 *
 * The refusal surfaces here rather than through the app-wide toast, in the
 * Alert the action bar's refused writes use: a rejected send belongs beside the
 * words still sitting in the box, and those words are what the rep would have
 * to retype if the box cleared on a refusal.
 */
function Composer({ threadId, contact }: { threadId: string; contact: Contact | undefined }) {
  const [draft, setDraft] = useState("");
  const send = useSendMessageMutation();
  const body = draft.trim();
  // A person with no consent on file cannot be texted (TMF-520), so the box is
  // off and says why; the seam would refuse the send anyway.
  const noConsent = textingBlocked(contact);
  const blocked = body === "" || send.isPending || noConsent !== null;

  // READ-ONLY rather than disabled while the send is out: a disabled input
  // loses focus, which would take the caret out of the box between the rep
  // pressing Enter and the vendor answering.
  const handleSend = () => {
    if (blocked) return;
    send.mutate({ threadId, body }, { onSuccess: () => setDraft("") });
  };

  // Typing retracts the refusal. It is the answer to the send the rep just
  // made, and the words it was about are being replaced.
  const handleType = (e: ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
    if (send.error !== null) send.reset();
  };

  return (
    <>
      <div className={styles.composerRow}>
        <Input
          className={styles.composerInput}
          value={draft}
          placeholder="Reply by text"
          aria-label="Reply by text"
          readOnly={send.isPending}
          disabled={noConsent !== null}
          onChange={handleType}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSend();
          }}
        />
        <Button
          type="button"
          variant="primary"
          size="md"
          disabled={blocked}
          onClick={handleSend}
        >
          Send
        </Button>
      </div>
      {noConsent !== null ? <Alert variant="warning">{noConsent}</Alert> : null}
      {send.error !== null ? (
        <Alert variant="danger">
          {isValidationError(send.error)
            ? formatValidationError(send.error)
            : "Couldn't send that. Try again."}
        </Alert>
      ) : null}
    </>
  );
}

/**
 * Texting the person first. The action folds onto the matter's live text
 * thread server-side, so a rep clicking it twice lands on the one conversation
 * rather than opening a second. It asks the consent of the matter's contact,
 * the person a new text conversation opens with.
 */
function TextAction({
  contact,
  matter,
}: {
  contact: Contact | undefined;
  matter: Case;
}) {
  const navigate = useNavigate();
  const createThread = useCreateThreadMutation();

  if (!contact?.phone) return null;
  const noConsent = textingBlocked(contact);

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      disabled={createThread.isPending || noConsent !== null}
      title={noConsent ?? undefined}
      onClick={() =>
        createThread.mutate(
          { case_id: matter.id, channel: "text" },
          {
            onSuccess: (t) =>
              void navigate({ to: "/alpha/threads/$threadId", params: { threadId: t.id } }),
          },
        )
      }
    >
      <MessageSquare size={14} />
      Text
    </Button>
  );
}

// Stage content by (channel, state): DESIGN.md §5's layout table.
function StageBody({
  thread,
  matter,
}: {
  thread: Thread;
  matter: Case;
}) {
  // Notes anchor to the MATTER (one running record per matter), so a
  // callback thread shows the notes the first call captured.
  const notesKey = caseNotesKey(matter.id);

  switch (thread.channel) {
    case "call":
      return (
        <>
          {thread.state === "waiting" && thread.subject ? (
            <div className={styles.pinnedBanner}>
              <PhoneOutgoing size={14} />
              {thread.subject}
            </div>
          ) : null}
          <TranscriptPane thread={thread} caseRow={matter}>
            {/* The recording appears once the vendor's post-hangup callback has
                landed and the audio is in the firm's bucket; until then it renders
                nothing. */}
            <RecordingPlayer thread={thread} />
          </TranscriptPane>
          <NoteCanvas storageKey={notesKey} />
        </>
      );

    case "text":
      return (
        <>
          <MessagePane thread={thread} contact={thread._embedded?.contact}>
            <Composer key={thread.id} threadId={thread.id} contact={thread._embedded?.contact} />
          </MessagePane>
          <NoteCanvas storageKey={notesKey} />
        </>
      );

    case "form":
      return (
        <>
          {/* A form thread has no composer: the firm answers a form arrival on
              a channel that can answer, a thread of its own (TMF-471). */}
          <FormPane thread={thread} />
          <NoteCanvas storageKey={notesKey} />
        </>
      );
  }
}

/** The rail's identity block: the matter's arrival facts and the person it
 *  talks to, always open, in its own dense Card at the Compass's density. */
function MatterDetails({ matter, contact }: { matter: Case; contact: Contact | undefined }) {
  return (
    <Card density="dense">
      <div className={styles.detailList}>
        <span className={heading}>Matter details</span>
        <Detail label="Phone" value={formatPhone(contact?.phone)} mono />
        <Detail label="Email" value={contact?.email || EMPTY_VALUE} />
        <Detail label="Source" value={matter.source ?? EMPTY_VALUE} />
        <Detail label="Arrived" value={new Date(matter.created_at).toLocaleString()} />
        <Detail label="Notes" value={matter.notes || EMPTY_VALUE} />
      </div>
    </Card>
  );
}

/** What a detail row shows for a value the matter does not have, the same
 *  glyph lib/dates.ts and lib/phones.ts answer with. */
const EMPTY_VALUE = "\u2014";

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={mono ? `${styles.detailValue} ${styles.phone}` : styles.detailValue}>
        {value}
      </span>
    </div>
  );
}
